import type { OverviewData } from './admin-overview-provider'

export type TrackCohort = OverviewData['completionByTrack'][number]
export type ThroughputRow = OverviewData['throughput'][number]

interface TrackMeta {
  label: string
  color: string
}

/**
 * Display metadata for the known engagement tracks (assignment `track` values).
 * Unknown tracks fall back to a title-cased label and a neutral color so the
 * charts never crash on a new track — they just render honestly.
 */
const TRACK_META: Record<string, TrackMeta> = {
  coding: { label: 'Coding', color: 'var(--cd-amber)' },
  system_design: { label: 'System design', color: 'var(--cd-purple)' },
  behavioral: { label: 'Behavioral', color: 'var(--cd-blue)' },
  task: { label: 'Task', color: 'var(--cd-text-muted)' },
}

export function trackMeta(track: string): TrackMeta {
  return (
    TRACK_META[track] ?? {
      label: track.charAt(0).toUpperCase() + track.slice(1),
      color: 'var(--cd-gray)',
    }
  )
}

/** Sorted, de-duplicated week numbers present across the cohorts. */
export function sortedWeekNumbers(cohorts: readonly TrackCohort[]): number[] {
  return [...new Set(cohorts.map((cohort) => cohort.weekNumber))].sort(
    (left, right) => left - right,
  )
}

/** Sorted, de-duplicated track keys present across the cohorts. */
export function sortedTracks(cohorts: readonly TrackCohort[]): string[] {
  return [...new Set(cohorts.map((cohort) => cohort.track))].sort(
    (left, right) => left.localeCompare(right),
  )
}

export interface CompletionSeries {
  track: string
  label: string
  color: string
  /** Percentage (0-100) per week in `weekNumbers` order; null ratios → 0. */
  points: number[]
}

/**
 * Groups cohorts into one line series per track. Each point is `ratio × 100`;
 * a null ratio (nothing scheduled that week for the track) renders as 0.
 */
export function deriveCompletionSeries(cohorts: readonly TrackCohort[]): {
  weekNumbers: number[]
  series: CompletionSeries[]
} {
  const weekNumbers = sortedWeekNumbers(cohorts)
  const byKey = new Map<string, TrackCohort>()
  for (const cohort of cohorts) {
    byKey.set(`${cohort.track}:${cohort.weekNumber}`, cohort)
  }

  const series = sortedTracks(cohorts).map((track) => {
    const meta = trackMeta(track)
    return {
      track,
      label: meta.label,
      color: meta.color,
      points: weekNumbers.map((week) => {
        const cohort = byKey.get(`${track}:${week}`)
        return cohort && cohort.ratio !== null
          ? Math.round(cohort.ratio * 100)
          : 0
      }),
    }
  })

  return { weekNumbers, series }
}

export interface TrackHealthRow {
  track: string
  label: string
  color: string
  scheduled: number
  completed: number
  /** Overall completion percent across all weeks (0-100). */
  percent: number
  status: 'healthy' | 'struggling'
  /** Undefined when fewer than two weeks of data exist for the track. */
  trend?: 'up' | 'down'
}

const HEALTHY_RATIO = 0.6

/**
 * Per-track health: overall ratio = sum(completed)/sum(scheduled) across weeks;
 * healthy at ≥ 60%. Trend compares the two most recent weeks' ratios (omitted
 * when only one week has data). Weeks with nothing scheduled (null ratio) never
 * enter the trend comparison.
 */
export function deriveTrackHealth(
  cohorts: readonly TrackCohort[],
): TrackHealthRow[] {
  return sortedTracks(cohorts).map((track) => {
    const rows = cohorts
      .filter((cohort) => cohort.track === track)
      .sort((left, right) => left.weekNumber - right.weekNumber)
    const scheduled = rows.reduce((sum, row) => sum + row.scheduled, 0)
    const completed = rows.reduce((sum, row) => sum + row.completed, 0)
    const ratio = scheduled === 0 ? 0 : completed / scheduled

    const scored = rows.filter((row) => row.ratio !== null)
    let trend: 'up' | 'down' | undefined
    if (scored.length >= 2) {
      const latest = scored[scored.length - 1].ratio as number
      const previous = scored[scored.length - 2].ratio as number
      trend = latest >= previous ? 'up' : 'down'
    }

    const meta = trackMeta(track)
    return {
      track,
      label: meta.label,
      color: meta.color,
      scheduled,
      completed,
      percent: Math.round(ratio * 100),
      status: ratio >= HEALTHY_RATIO ? 'healthy' : 'struggling',
      trend,
    }
  })
}

export interface PaceRow {
  weekNumber: number
  planned: number
  completed: number
  /** completed / planned as a percent (0-100), capped at 100 for the bar. */
  percent: number
  onPace: boolean
}

/**
 * Per-week pace: planned = sum of scheduled assignments that week (across
 * tracks); completed = throughput completions that week. Weeks with nothing
 * planned are excluded (a null-denominator never enters the ratio). Most-recent
 * `limit` weeks are returned, oldest-first.
 */
export function derivePace(
  cohorts: readonly TrackCohort[],
  throughput: readonly ThroughputRow[],
  limit = 4,
): PaceRow[] {
  const plannedByWeek = new Map<number, number>()
  for (const cohort of cohorts) {
    plannedByWeek.set(
      cohort.weekNumber,
      (plannedByWeek.get(cohort.weekNumber) ?? 0) + cohort.scheduled,
    )
  }
  const completedByWeek = new Map<number, number>()
  for (const row of throughput) {
    completedByWeek.set(row.weekNumber, row.completed)
  }

  const rows: PaceRow[] = []
  for (const [weekNumber, planned] of plannedByWeek) {
    if (planned <= 0) continue
    const completed = completedByWeek.get(weekNumber) ?? 0
    rows.push({
      weekNumber,
      planned,
      completed,
      percent: Math.min(Math.round((completed / planned) * 100), 100),
      onPace: completed >= planned,
    })
  }

  return rows
    .sort((left, right) => left.weekNumber - right.weekNumber)
    .slice(-limit)
}
